import { ActivationFormat } from "./brands";

// Brand opportunities visible to communities
export interface BrandOpportunity {
  id: string;
  brandId: string;
  brandName: string;
  brandLogo: string;
  brandIndustry: string;
  title: string;
  format: ActivationFormat;
  budgetRange: string;
  budgetMin: number;
  budgetMax: number;
  targetCity: string;
  desiredThemes: string[];
  desiredValues: string[];
  communitySize: string;
  deadline: string;
  status: "open" | "closing-soon";
  campaignObjective: string;
  brandStory: string;
  whyCommunities: string;
  expectedAudience: string;
  dates: string;
  deliverables: string[];
  successMetrics: string[];
  fitScore?: number;
  badges: ("high-fit" | "new" | "urgent")[];
}

export const brandOpportunities: BrandOpportunity[] = [
  {
    id: "opp-001", brandId: "hoka", brandName: "Hoka Running", brandLogo: "/placeholder.svg", brandIndustry: "Sport",
    title: "Trail Running Collection Launch", format: "sponsorship",
    budgetRange: "€3,000 – €5,000", budgetMin: 3000, budgetMax: 5000,
    targetCity: "Paris", desiredThemes: ["Running", "Fitness", "Wellness"], desiredValues: ["Community", "Wellness", "Inclusivity"],
    communitySize: "200+", deadline: "2024-09-15", status: "open",
    campaignObjective: "Generate authentic UGC and product feedback for the new Speedgoat 6 trail shoe.",
    brandStory: "Hoka believes running should feel effortless. We design maximally cushioned shoes that let every runner fly.",
    whyCommunities: "We want real runners, not influencers. Communities give us authentic stories and honest feedback.",
    expectedAudience: "60-120 participants", dates: "October 5-6, 2024",
    deliverables: ["Host a group run with Hoka products", "Collect participant feedback forms", "Provide event photos"],
    successMetrics: ["GIR > 70%", "Purchase intent > 60%", "Min 50 participants"],
    fitScore: 92, badges: ["high-fit"],
  },
  {
    id: "opp-002", brandId: "feed", brandName: "Feed", brandLogo: "/placeholder.svg", brandIndustry: "Food",
    title: "New Plant-Based Bar Tasting", format: "testing",
    budgetRange: "€1,000 – €2,500", budgetMin: 1000, budgetMax: 2500,
    targetCity: "Lyon", desiredThemes: ["Food", "Wellness", "Sustainability"], desiredValues: ["Sustainability", "Innovation"],
    communitySize: "100+", deadline: "2024-08-30", status: "closing-soon",
    campaignObjective: "Get real consumer feedback before national launch of new plant-based energy bar.",
    brandStory: "Feed is a French startup reimagining nutrition with smart, balanced, and plant-based food.",
    whyCommunities: "Communities give us unfiltered feedback from real consumers, not focus groups.",
    expectedAudience: "30-60 participants", dates: "September 14, 2024",
    deliverables: ["Organize a tasting session", "Distribute feedback surveys", "Share 3-5 event photos"],
    successMetrics: ["Min 30 taste testers", "Feedback completion > 80%", "Purchase intent > 65%"],
    fitScore: 85, badges: ["urgent"],
  },
  {
    id: "opp-003", brandId: "typology", brandName: "Typology", brandLogo: "/placeholder.svg", brandIndustry: "Beauty",
    title: "Skincare Routine Workshop Series", format: "co-creation",
    budgetRange: "€2,500 – €4,000", budgetMin: 2500, budgetMax: 4000,
    targetCity: "Paris", desiredThemes: ["Wellness", "Lifestyle"], desiredValues: ["Sustainability", "Wellness", "Creativity"],
    communitySize: "150+", deadline: "2024-10-01", status: "open",
    campaignObjective: "Co-create skincare workshop content with wellness communities to build brand trust.",
    brandStory: "Typology makes skincare simple with ingredient-first formulas backed by science.",
    whyCommunities: "We want to educate, not sell. Communities are the perfect space for honest skincare conversations.",
    expectedAudience: "40-80 participants", dates: "October 12-13, 2024",
    deliverables: ["Co-host a workshop on skincare routines", "Collect participant testimonials", "Share UGC content"],
    successMetrics: ["GIR > 75%", "Brand sentiment score > 80%", "Min 40 participants"],
    fitScore: 88, badges: ["high-fit", "new"],
  },
  {
    id: "opp-004", brandId: "asphalte", brandName: "Asphalte", brandLogo: "/placeholder.svg", brandIndustry: "Fashion",
    title: "Urban Style Photography Walk", format: "branded-content",
    budgetRange: "€1,500 – €3,000", budgetMin: 1500, budgetMax: 3000,
    targetCity: "Marseille", desiredThemes: ["Art", "Culture", "Photography"], desiredValues: ["Creativity", "Urban culture"],
    communitySize: "50+", deadline: "2024-09-20", status: "open",
    campaignObjective: "Create authentic visual content featuring Asphalte clothing in urban settings.",
    brandStory: "Asphalte builds wardrobe essentials that last, using pre-orders to eliminate waste.",
    whyCommunities: "We want real people wearing our clothes in real life — not staged photoshoots.",
    expectedAudience: "25-50 participants", dates: "September 28, 2024",
    deliverables: ["Organize a photo walk in urban locations", "Provide 10+ edited photos", "Share participant impressions"],
    successMetrics: ["Min 20 content pieces", "Engagement rate > 5%", "Brand recall > 70%"],
    fitScore: 74, badges: ["new"],
  },
  {
    id: "opp-005", brandId: "swile", brandName: "Swile", brandLogo: "/placeholder.svg", brandIndustry: "Tech",
    title: "Tech Community Ambassador Program", format: "ambassador",
    budgetRange: "€3,000 – €6,000", budgetMin: 3000, budgetMax: 6000,
    targetCity: "Bordeaux", desiredThemes: ["Tech", "Business", "Innovation"], desiredValues: ["Innovation", "Networking"],
    communitySize: "300+", deadline: "2024-10-15", status: "open",
    campaignObjective: "Build a network of tech community ambassadors to drive product adoption.",
    brandStory: "Swile modernizes employee benefits with a smart, all-in-one platform.",
    whyCommunities: "Tech communities are our core users. We want authentic advocates, not paid promoters.",
    expectedAudience: "80-150 participants", dates: "November 2024 (ongoing)",
    deliverables: ["Identify 5-10 ambassadors", "Host monthly meetups", "Track referral conversions"],
    successMetrics: ["Min 5 active ambassadors", "Referral conversion > 15%", "NPS > 70"],
    fitScore: 82, badges: ["high-fit"],
  },
  {
    id: "opp-006", brandId: "feed", brandName: "Feed", brandLogo: "/placeholder.svg", brandIndustry: "Food",
    title: "Affiliate Partnership for Fitness Communities", format: "affiliate",
    budgetRange: "€500 – €1,500", budgetMin: 500, budgetMax: 1500,
    targetCity: "All Cities", desiredThemes: ["Fitness", "Running", "Wellness"], desiredValues: ["Wellness", "Community"],
    communitySize: "100+", deadline: "2024-11-01", status: "open",
    campaignObjective: "Drive online sales through community-specific affiliate links and discount codes.",
    brandStory: "Feed provides smart nutrition for active people who don't have time to compromise.",
    whyCommunities: "Community leaders are trusted voices. Their recommendations convert better than ads.",
    expectedAudience: "Unlimited", dates: "Ongoing from October 2024",
    deliverables: ["Share affiliate links with members", "Post about Feed products monthly", "Track redemption rates"],
    successMetrics: ["Min 50 conversions/month", "Commission payout > €200/month", "Retention > 3 months"],
    fitScore: 79, badges: [],
  },
];
